import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import stylePropType from 'react-style-proptype';

import { Label } from '../../atoms';

const ButtonElement = styled.button.attrs((props) => ({ type: props.type || 'button' }))`
  position: relative;
  font-family: var(--font-family-title);

  padding: ${({ pill }) => (pill ? '0 8px' : '0 20px')};
  cursor: pointer;

  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  height: ${(props) => (props.small ? '32px' : '40px')};
  width: ${(props) => (props.block ? '100%' : 'auto')};
  border: none;
  border-radius: 10px;

  transition: border-color 200ms linear, background-color 200ms linear,
    background-position 200ms linear, opacity 100ms linear, box-shadow 200ms linear;

  & > ${Label} {
    cursor: pointer;
    column-gap: 8px;
    font-weight: 600;
  }

  ${(props) => {
    switch (true) {
      case props.secondary:
        return `
            background-color: var(--wash-orange-default);
            & > ${Label} {
              color: var(--primary-default);
            }

            &:hover:not(:active) {
              background-color: var(--wash-orange-hover);
            }
            &:active {
              background-color: var(--wash-orange-pressed);
              box-shadow: rgba(229, 96, 0, 0.2) 0 1px 2px 0 inset, rgba(255, 255, 255, 0.25) 0 1px 0 0;
            }
          `;
      case props.tertiary:
        return `
            background-color: var(--wash-gray-default);
            & > ${Label} {
              color: var(--content-title);
            }

            &:hover:not(:active) {
              background-color: var(--wash-gray-hover);
            }
            &:active {
              background-color: var(--wash-gray-pressed);
              box-shadow: rgba(0, 0, 0, 0.2) 0 1px 2px 0 inset, rgba(255, 255, 255, 0.25) 0 1px 0 0;
            }
          `;
      case props.naked:
        return `
            background-color: transparent;
            & > ${Label} {
              color: var(--content-title);
            }

            &:hover:not(:active) {
              background-color: var(--wash-gray-hover);
            }
            &:active {
              background-color: var(--wash-gray-pressed);
              box-shadow: rgba(0, 0, 0, 0.2) 0 1px 2px 0 inset, rgba(255, 255, 255, 0.25) 0 1px 0 0;
            }
          `;
      case props.critical && props.small:
        return `
            background-color: var(--critical-default);
            & > ${Label} {
              color: var(--white);
            }

            &:hover:not(:active) {
              background-color: var(--critical-hover);
            }
            &:active {
              background-color: var(--critical-pressed);
              box-shadow: rgba(0, 0, 0, 0.2) 0 1px 2px 0 inset, rgba(255, 255, 255, 0.25) 0 1px 0 0;
            }
          `;
      case props.critical && !props.small:
        return `
            background-color: var(--wash-red-default);
            & > ${Label} {
              color: var(--critical-default);
            }

            &:hover:not(:active) {
              background-color: var(--wash-red-hover);
            }
            &:active {
              background-color: var(--wash-red-pressed);
              box-shadow: rgba(0, 0, 0, 0.1) 0 1px 2px 0 inset, rgba(255, 255, 255, 0.25) 0 1px 0 0;
            }
        `;
      default:
        return `
            background-color: var(--primary-default);
            & > ${Label} {
              color: var(--white);
            }

            &:hover:not(:active) {
              background-color: var(--primary-hover);
            }
            &:active {
              background-color: var(--primary-pressed);
              box-shadow: rgba(0, 0, 0, 0.2) 0 1px 2px 0 inset, rgba(255, 255, 255, 0.25) 0 1px 0 0;
            }
          `;
    }
  }}}

  &:disabled {
    cursor: not-allowed;
    background-color: var(--gray-20);
    color: var(--white);
    box-shadow: none;

    &:hover,&:active {
      background-color: var(--gray-20);
      color: var(--white);
    }
  }
`;

const Button = forwardRef(
  (
    {
      children = null,
      className = null,
      type = 'button',
      onClick = null,
      block = false,
      small = false,
      pill = false,
      secondary = false,
      tertiary = false,
      naked = false,
      critical = false,
      labelStyle = null,
      disabled = false
    },
    ref
  ) => (
    <ButtonElement
      ref={ref}
      className={className}
      type={type}
      onClick={onClick && ((evt) => onClick(evt))}
      block={block}
      small={small}
      pill={pill}
      secondary={secondary}
      tertiary={tertiary}
      naked={naked}
      critical={critical}
      disabled={disabled}
    >
      <Label $small={small} style={labelStyle}>
        {children}
      </Label>
    </ButtonElement>
  )
);

Button.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  className: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  block: PropTypes.bool,
  small: PropTypes.bool,
  pill: PropTypes.bool,
  secondary: PropTypes.bool,
  tertiary: PropTypes.bool,
  naked: PropTypes.bool,
  critical: PropTypes.bool,
  labelStyle: stylePropType
};

export { Button };
